import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConnectivityProvider } from "./context/ConnectivityContext";
import { OfflineProvider, useOffline } from "./context/OfflineContext";
import { NotificationProvider } from "./context/NotificationContext";
import ConnectionStatus from "./components/ConnectionStatus";
import SyncIndicator from "./components/SyncIndicator";
import WebPushOptIn from "./components/WebPushOptIn";
import { pushNotificationService } from "./services/pushNotifications";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Modules from "./pages/Modules";
import ModuleDetail from "./pages/ModuleDetail";
import LessonView from "./pages/LessonView";
import LessonQuizEnhanced from "./pages/LessonQuizEnhanced";
import QuizView from "./pages/QuizView";
import Calendar from "./pages/Calendar";
import Health from "./pages/Health";
import Notifications from "./pages/Notifications";
import NotificationsManager from "./pages/NotificationsManager";
import AdminDashboard from "./pages/AdminDashboard";
import Partners from "./pages/Partners";
import PartnerApply from "./pages/PartnerApply";
import Leaderboard from "./pages/Leaderboard";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Security from "./pages/Security";
import GirlsRoom from "./pages/GirlsRoom";
import Community from "./pages/Community";
import TipsAndTricks from "./pages/TipsAndTricks";
import ChallengesAndRewards from "./pages/ChallengesAndRewards";
import CycleHistory from "./pages/CycleHistory";
import PushNotificationTest from "./pages/PushNotificationTest";
import CustomAlerts from "./pages/CustomAlerts";
import GirlsRoomLeaderboard from "./pages/GirlsRoomLeaderboard";
import AdminTipsManager from "./pages/AdminTipsManager";
import InstallPrompt from "./components/InstallPrompt";

// Register service worker for offline support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        console.log('SW registered:', registration.scope);
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

// Initialize push notifications when user is authenticated
function PushNotificationInitializer() {
  const { isAuthenticated, token } = useAuth();
  const { preCacheContent, isOnline } = useOffline();
  
  useEffect(() => {
    if (isAuthenticated) {
      pushNotificationService.initialize();
    }
  }, [isAuthenticated]);
  
  // Pre-cache content separately to avoid loop
  useEffect(() => {
    if (isAuthenticated && token && isOnline) {
      // Add slight delay to avoid race conditions
      const timer = setTimeout(() => {
        preCacheContent(token);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, token, isOnline]); // Don't include preCacheContent in deps
  
  return null;
}

function App() {
  return (
    <div className="App">
      <ConnectivityProvider>
      <AuthProvider>
        <OfflineProvider>
          <NotificationProvider>
          <ConnectionStatus />
          <SyncIndicator />
          <WebPushOptIn />
          <PushNotificationInitializer />
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/notifications-manager" element={<NotificationsManager />} />
            <Route path="/modules" element={<Modules />} />
            <Route path="/module/:moduleId" element={<ModuleDetail />} />
            <Route path="/lesson/:lessonId" element={<LessonView />} />
            <Route path="/lesson/:lessonId/quiz" element={<LessonQuizEnhanced />} />
            <Route path="/quiz/:moduleId" element={<QuizView />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/health" element={<Health />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/partners/apply" element={<PartnerApply />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/security" element={<Security />} />
            <Route path="/girls-room" element={<GirlsRoom />} />
            <Route path="/community" element={<Community />} />
            <Route path="/tips" element={<TipsAndTricks />} />
            <Route path="/challenges" element={<ChallengesAndRewards />} />
            <Route path="/cycle-history" element={<CycleHistory />} />
            <Route path="/push-test" element={<PushNotificationTest />} />
            <Route path="/custom-alerts" element={<CustomAlerts />} />
            <Route path="/girls-room-leaderboard" element={<GirlsRoomLeaderboard />} />
            <Route path="/admin/tips" element={<AdminTipsManager />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
        <InstallPrompt />
        </NotificationProvider>
        </OfflineProvider>
      </AuthProvider>
      </ConnectivityProvider>
    </div>
  );
}

export default App;
